import { Col, Row, Table, message } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { getTillStats, getTillProductStats } from "../../api/TillStats";
import PrintButton from "../UI/PrintButton/printbutton";

interface TransactionInterface {
  key: string;
  currency: string;
  symbol: string;
  initialAmount: string;
  salesTotalAmount: string;
  receiptsTotalAmount: string;
  expectedAmount: string;
  deliveryAmount: string;
  netAmount: string;
  netExpected: string;
}

interface ProductsInterface {
  key: string;
  warehouse: string;
  category: string;
  product: string;
  barcode: string;
  quantity: number;
  sales: string;
}

export const TransactionList = () => {
  const { t } = useTranslation("common");

  const [tillData, setTillData] = useState<TransactionInterface[]>([]);
  const [productData, setproductData] = useState<ProductsInterface[]>([]);
  const [pagination, setPagination] = useState<{ current: number, pageSize: number, total: number, limit: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
    limit: 10,
  });

  const currentTillId = localStorage.getItem("tillId");

  const { mutateAsync: getTillStatsMutation, isLoading: isTillStatsLoading } =
    useMutation("getTillStats", getTillStats);

  const { mutateAsync: getTillProductStatsMutation, isLoading: isTillProductLoading } = useMutation(
    "getTillProductStats",
    ({ tillId, limit, offset }: { tillId: number; limit?: number, offset?: number }) =>
      getTillProductStats({ tillId, limit, offset }),
    {
      onSuccess: ({ data }) => {
        setproductData(data.data);
      },
      onError: () => {
        message.error('cannot load data')
      }
    }
  );

  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
    getTillProductStatsMutation({ tillId: Number(currentTillId), limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize })
  };

  useEffect(() => {
    if (currentTillId) {
      getTillStatsMutation(Number(currentTillId)).then((res: any) => {
        // Assuming you have separate arrays for different properties
        const initialTill = res.data.initialTill;
        const salesAmount = res.data.salesAmount;
        const receiptsAmount = res.data.receiptsAmount;
        const expectedAmount = res.data.expectedAmount;
        const deliveryAmount = res.data.delivery;
        const netAmount = res.data.netAmount;
        const NetExpectedAmount = res.data.netExpected;

        // Mapping fetched data to match TransactionInterface structure
        const mappedData: TransactionInterface[] = initialTill.map(
          (initial: any, index: any) => ({
            key: index.toString(),
            currency: initial.currency.name,
            symbol: initial.currency.symbol,
            initialAmount: initial.totalAmountText,
            salesTotalAmount: salesAmount[index].totalAmountText,
            receiptsTotalAmount: receiptsAmount[index].totalAmountText,
            expectedAmount: expectedAmount[index].totalAmountText,
            deliveryAmount: deliveryAmount[index].totalDeliveryText,
            netAmount: netAmount[index].totalNetAmountText,
            netExpected: NetExpectedAmount[index].totalNetExpectedText
          })
        );
        setTillData(mappedData);
      });

      getTillProductStatsMutation({ tillId: Number(currentTillId), limit: pagination.limit }).then(res => {
   
      })
    }
  },[]);

  const handlePrint = () => {
    if (tillData) {
      const printStyles = `
      <style>
          @page {
            size: auto;
            margin: 0mm;
          }
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          
          .print-table th, .print-table td {
            border: 1px solid #ddd;
            text-align: center;
          }
        
      </style>
    `;
      const printContent = `
      <table class="print-table">
      <thead>
      <tr>
      <th>Currency</th>
      <th>Total Intial</th>
      <th>Total Sales</th>
      <th>Total Delivery</th>
      <th>Net Sales</th>
      <th>Total Receipts</th>
      <th>Total Expected</th>
      <th>Net Expected</th>
      </tr>
      </thead>
      <tbody>
     ${tillData
          .map(
            (till) => `
      <tr>
      <td>${till.currency}</td>
      <td>${till.initialAmount}</td>
      <td>${till.salesTotalAmount}</td>
      <td>${till.deliveryAmount}</td>
      <td>${till.netAmount}</td>      
      <td>${till.receiptsTotalAmount}</td>
      <td>${till.expectedAmount}</td>
      <td>${till.netExpected}</td>
      </tr>
      `
          )
          .join("")}
      </tbody>
      </table>
`;
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      const iframeDocument =
        iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDocument) {
        iframeDocument.open();
        iframeDocument.write(printStyles);
        iframeDocument.write(printContent);
        iframeDocument.close();
        // Listen for the "afterprint" event to remove the iframe after printing or cancelling
        iframe.contentWindow?.addEventListener("afterprint", () => {
          document.body.removeChild(iframe);
        });
        iframe.contentWindow?.print();
      }
    }
  };

  const columns: ColumnsType<TransactionInterface> = [
    {
      title: `${t("currency")}`,
      dataIndex: "currency",
      key: "till-currency",
      className: "table-header",
      width: "9rem"
    },
    {
      title: `${t("total-initial")}`,
      dataIndex: "initialAmount",
      key: "till-initialAmount",            
      align: "center",
    },
    {
      title: `${t("total-sale")}`,
      dataIndex: "salesTotalAmount",
      key: "till-salesTotalAmount",
      align: "center",
    },
    {
      title: `${t("total-delivery")}`,
      dataIndex: "deliveryAmount",
      key: "till-deliveryAmount",
      align: "center",
    },
    {
      title: `${t("net-sales")}`,
      dataIndex: "netAmount",
      key: "till-netAmount",
      align: "center",
    },
    {
      title: `${t("total-receipts")}`,
      dataIndex: "receiptsTotalAmount",
      key: "till-receiptsTotalAmount",
      align: "center",
    },
    {
      title: `${t("total-expected")}`,
      dataIndex: "expectedAmount",
      key: "till-expectedAmount",
      align: "center",
    },
    {
      title: `${t("net-expected")}`,
      dataIndex: "netExpected",
      key: "till-netExpected",
      align: "center",    
    },
  ];


  const columnsProducts: ColumnsType<ProductsInterface> = [
    {
      title: `${t("warehouse")}`,
      dataIndex: "warehouse",
      key: "product-warehouse",
      className: "table-header",
      width: "9rem"
    },
    {
      title: `${t("category")}`,
      dataIndex: "category",
      key: "product-category",            
      align: "center",
    },
    {
      title: `${t("product")}`,
      dataIndex: "product",
      key: "product-product",
      align: "center",
    },
    {
      title: `${t("barcode")}`,
      dataIndex: "barcode",
      key: "product-barcode",
      align: "center",
    },
    {
      title: `${t("quantity")}`,
      dataIndex: "quantity",
      key: "product-quantity",
      align: "center",
    },
    {
      title: `${t("sales")}`,
      dataIndex: "priceText",
      key: "product-priceText",
      align: "center",
    },
  ];

  return (
    <>
      <Row
        gutter={[10, 0]}
        style={{ width: "100%", marginBottom: "10px" }}
        justify="center"
      >
        <Col offset={24} span={1}>
          <PrintButton onClick={handlePrint} />
        </Col>
      </Row>
      <Table
        rowKey={(record) => record.currency}
        scroll={{ x: 1000 }}
        className="till-list"
        columns={columns}
        pagination={false}
        dataSource={tillData}
        loading={isTillStatsLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
      />
      <Row style={{ marginTop: "30px" , marginBottom: "10px" }}>
        {t("items-summary")}
      </Row>
      <Table
        rowKey={(record) => record.barcode}
        scroll={{ x: 1000 }}
        className="till-products"
        columns={columnsProducts}
        pagination={{ ...pagination, showSizeChanger: false }}
        dataSource={productData}
        loading={isTillProductLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={handleTableChange}
      />      
    </>
  );
};
